/**
 * Oji Matcha — PDP Reviews
 *
 * Vanilla JS controller for the native reviews section.
 * - Sort dropdown (Most Recent / Highest / Lowest)
 * - Filter chips (All / 5..1 stars / With photos)
 * - Load more pagination
 * - Defers heavy work until the section enters the viewport via IntersectionObserver
 *
 * No jQuery, no globals.
 */

(function () {
  'use strict';

  function initSection(section) {
    if (!section || section.dataset.ojiReviewsInit === 'true') {
      return;
    }
    section.dataset.ojiReviewsInit = 'true';

    var perPage = parseInt(section.dataset.perPage, 10);
    if (isNaN(perPage) || perPage < 1) {
      perPage = 6;
    }

    var list = section.querySelector('[data-reviews-list]');
    if (!list) {
      return;
    }

    var sortSelect = section.querySelector('[data-reviews-sort]');
    var filterButtons = section.querySelectorAll('[data-reviews-filter]');
    var loadMoreBtn = section.querySelector('[data-reviews-load-more]');
    var noResults = section.querySelector('[data-reviews-no-results]');

    var allItems = Array.prototype.slice.call(list.querySelectorAll('.oji-pdp-reviews__item'));
    var currentFilter = 'all';
    var currentSort = 'recent';
    var visibleCount = perPage;

    function matchesFilter(item) {
      if (currentFilter === 'all') {
        return true;
      }
      if (currentFilter === 'photos') {
        return item.dataset.hasPhoto === 'true';
      }
      return String(item.dataset.rating) === String(currentFilter);
    }

    function compareItems(a, b) {
      var ra = parseFloat(a.dataset.rating) || 0;
      var rb = parseFloat(b.dataset.rating) || 0;
      var da = parseInt(a.dataset.date, 10) || 0;
      var db = parseInt(b.dataset.date, 10) || 0;

      if (currentSort === 'highest') {
        if (rb !== ra) return rb - ra;
        return db - da;
      }
      if (currentSort === 'lowest') {
        if (ra !== rb) return ra - rb;
        return db - da;
      }
      // recent (default)
      return db - da;
    }

    function render() {
      // Build filtered set
      var filtered = allItems.filter(matchesFilter);
      filtered.sort(compareItems);

      // Reorder DOM (detach all then append in sorted order)
      var fragment = document.createDocumentFragment();
      filtered.forEach(function (item) {
        fragment.appendChild(item);
      });
      // Items not matching filter still need to live somewhere; append them last (they will be hidden anyway)
      allItems.forEach(function (item) {
        if (filtered.indexOf(item) === -1) {
          fragment.appendChild(item);
        }
      });
      list.appendChild(fragment);

      // Apply visibility (use hidden attribute, not display:none, for a11y)
      var shown = 0;
      allItems.forEach(function (item) {
        if (matchesFilter(item) && shown < visibleCount) {
          item.removeAttribute('hidden');
          shown += 1;
        } else {
          item.setAttribute('hidden', '');
        }
      });

      // No results state
      if (filtered.length === 0) {
        if (noResults) noResults.removeAttribute('hidden');
      } else {
        if (noResults) noResults.setAttribute('hidden', '');
      }

      // Load more visibility
      if (loadMoreBtn) {
        if (filtered.length > visibleCount) {
          loadMoreBtn.removeAttribute('hidden');
        } else {
          loadMoreBtn.setAttribute('hidden', '');
        }
      }
    }

    // Sort handler
    if (sortSelect) {
      sortSelect.addEventListener('change', function (e) {
        currentSort = e.target.value;
        visibleCount = perPage;
        render();
      });
    }

    // Filter handlers
    Array.prototype.forEach.call(filterButtons, function (btn) {
      btn.addEventListener('click', function () {
        var value = btn.getAttribute('data-reviews-filter');
        currentFilter = value;
        visibleCount = perPage;

        Array.prototype.forEach.call(filterButtons, function (other) {
          if (other === btn) {
            other.classList.add('is-active');
            other.setAttribute('aria-pressed', 'true');
          } else {
            other.classList.remove('is-active');
            other.setAttribute('aria-pressed', 'false');
          }
        });

        render();
      });
    });

    // Load more handler
    if (loadMoreBtn) {
      loadMoreBtn.addEventListener('click', function () {
        visibleCount += perPage;
        render();
      });
    }

    // Initial render is technically already correct from server-side Liquid,
    // but call render() once so JS state matches DOM exactly.
    render();
  }

  function setupObserver() {
    var sections = document.querySelectorAll('.oji-pdp-reviews');
    if (!sections.length) {
      return;
    }

    if (!('IntersectionObserver' in window)) {
      // Fallback: init immediately
      Array.prototype.forEach.call(sections, initSection);
      return;
    }

    var observer = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          initSection(entry.target);
          obs.unobserve(entry.target);
        }
      });
    }, { rootMargin: '200px 0px' });

    Array.prototype.forEach.call(sections, function (section) {
      observer.observe(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupObserver);
  } else {
    setupObserver();
  }

  /**
   * Post-submit auto-refresh (Reviews UI v2 / W3).
   *
   * assets/oji-review-form.js calls window.__ojiReviewsRefresh() after a
   * successful review submission (falling back to location.reload() if the
   * global is undefined). This defines it: swap the reviews wall + the
   * buy-box star badge in place via Shopify's Section Rendering API
   * (?sections=<id1>,<id2>), instead of a full page reload.
   *
   * IMPORTANT — do not add `Accept: application/json` to the fetch: on a
   * product page URL that header routes Shopify to the legacy product.json
   * content-negotiation response (ProductDetailsController) and SILENTLY
   * IGNORES the ?sections= param, verified empirically against the dev
   * server. Omit the header (default Accept) to get the real sections map.
   */
  function sectionIdFor(selector) {
    var el = document.querySelector(selector);
    var wrapper = el && el.closest('.shopify-section');
    if (!wrapper || !wrapper.id) return null;
    return wrapper.id.replace(/^shopify-section-/, '');
  }

  // ?sections= HTML embeds <script> tags (Turnstile, JSON-LD,
  // oji-pdp-reviews.js, oji-review-form.js) that never execute on innerHTML
  // assignment. Re-create each node so they run, same pattern as
  // assets/oji-collection-editorial.js runInjectedScripts (lines 23-34).
  function runInjectedScripts(container) {
    Array.prototype.forEach.call(container.querySelectorAll('script'), function (old) {
      var script = document.createElement('script');
      Array.prototype.forEach.call(old.attributes, function (attr) {
        script.setAttribute(attr.name, attr.value);
      });
      if (!old.src) {
        script.textContent = old.textContent;
      }
      old.parentNode.replaceChild(script, old);
    });
  }

  // Swaps the *contents* of an already-live `#shopify-section-<id>` wrapper.
  // The ?sections= response for a given id is itself the full
  // `#shopify-section-<id>` wrapper element (verified empirically — it is
  // NOT innerHTML-only), so naively assigning it as innerHTML would nest a
  // second wrapper inside the first. Parse it, pull out that same wrapper
  // from the parsed document, and copy its innerHTML instead.
  function swapSectionContents(id, html) {
    var target = document.getElementById('shopify-section-' + id);
    if (!target) return false;
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var source = parsed.getElementById('shopify-section-' + id) || parsed.body.firstElementChild;
    if (!source) return false;
    target.innerHTML = source.innerHTML;
    runInjectedScripts(target);
    return true;
  }

  // The buy-box section (#oji-v4-shop) also contains the variant picker,
  // gallery thumbnails and "Comprar ahora" form — all bound with direct
  // (non-delegated) listeners by assets/oji-v4.js once on page load. A
  // whole-section innerHTML swap there would silently kill add-to-cart and
  // gallery interactivity with no re-init hook to recover them (unlike the
  // reviews section, oji-v4.js has no readyState-aware re-bootstrap). So the
  // buy box only ever gets its `.shop__rating` badge patched in place —
  // never a full section swap.
  function refreshBuyBadge(html) {
    var panel = document.querySelector('#oji-v4-shop .shop__panel');
    if (!panel) return;
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var newBadge = parsed.querySelector('.shop__rating');
    var liveBadge = panel.querySelector('.shop__rating');

    if (!newBadge) {
      if (liveBadge) liveBadge.remove();
      return;
    }
    var imported = document.importNode(newBadge, true);
    if (liveBadge) {
      liveBadge.replaceWith(imported);
    } else {
      var heading = panel.querySelector('h2.h2');
      if (heading) {
        heading.insertAdjacentElement('afterend', imported);
      } else {
        panel.insertBefore(imported, panel.firstChild);
      }
    }
  }

  function refreshReviews() {
    var reviewsId = sectionIdFor('.oji-pdp-reviews');
    var buyId = sectionIdFor('#oji-v4-shop');

    if (!reviewsId) {
      location.reload();
      return;
    }

    var ids = buyId ? [reviewsId, buyId] : [reviewsId];

    fetch(window.location.pathname + '?sections=' + ids.join(','))
      .then(function (res) {
        if (!res.ok) throw new Error('sections fetch failed: ' + res.status);
        return res.json();
      })
      .then(function (data) {
        var ok = data && typeof data[reviewsId] === 'string' && swapSectionContents(reviewsId, data[reviewsId]);
        if (!ok) {
          location.reload();
          return;
        }

        if (buyId && typeof data[buyId] === 'string') {
          refreshBuyBadge(data[buyId]);
        }

        var target = document.getElementById('shopify-section-' + reviewsId);
        if (target && typeof target.scrollIntoView === 'function') {
          var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          target.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
        }
      })
      .catch(function () {
        location.reload();
      });
  }

  window.__ojiReviewsRefresh = refreshReviews;
})();
